/**
 * EmployeeGrid.js
 * Location: src/js/components/views/EmployeeGrid.js
 *
 * Renders the employee grid + pagination entirely client-side from JSON
 * (initial data embedded on the page, then REST responses), the same way
 * TypesenseSearch.js renders hits/pagination from InstantSearch state —
 * one render function used for the first paint and every update, with
 * pagination click handlers rebound on every render.
 */
class EmployeeGrid {
  constructor(el) {
    this.el = el;
    this.restUrl = el.dataset.restUrl;
    this.perPage = parseInt(el.dataset.perPage, 10) || 12;
    this.placeholder = '';

    this.layout = this.el.querySelector('.employee-grid__layout');
    this.sidebar = this.el.querySelector('.employee-grid__sidebar');
    this.gridEl = this.el.querySelector('.employee-grid__grid');
    this.paginationMount = this.el.querySelector(
      '.employee-grid__pagination-mount'
    );
    this.mobileSlot = this.el.querySelector(
      '.employee-grid__mobile-filters-slot'
    );

    this._initFilterPanels();
    this._initMobile();
    this._bindFilters();
    this._bindClearAll();
    this._bindFilterPanelClear();

    this._render(this._readInitialData());
  }

  _readInitialData() {
    const script = this.el.querySelector('.employee-grid__initial-data');

    if (!script) {
      return { employees: [], pagination: { current: 1, total: 1 } };
    }

    try {
      const data = JSON.parse(script.textContent);
      this.placeholder = data.placeholder || '';
      return data;
    } catch (err) {
      window.console.warn('[EmployeeGrid] Failed to parse initial data:', err);
      return { employees: [], pagination: { current: 1, total: 1 } };
    }
  }

  _initFilterPanels() {
    const toggles = this.el.querySelectorAll('.employee-grid__filter-toggle');

    toggles.forEach((btn, index) => {
      btn.addEventListener('click', () => {
        const panel = btn.closest('.employee-grid__filter-panel');
        const isOpen = panel.classList.toggle(
          'employee-grid__filter-panel--open'
        );
        btn.setAttribute('aria-expanded', String(isOpen));
      });

      // Only open the first panel by default
      if (index === 0) btn.click();
    });
  }

  _initMobile() {
    const moveSidebar = () => {
      if (!this.sidebar || !this.mobileSlot || !this.layout) return;

      if (window.innerWidth <= 767) {
        if (!this.mobileSlot.contains(this.sidebar)) {
          this.mobileSlot.appendChild(this.sidebar);
        }
      } else if (!this.layout.contains(this.sidebar)) {
        this.layout.insertBefore(this.sidebar, this.layout.firstChild);
      }
    };

    moveSidebar();
    window.addEventListener('resize', moveSidebar);

    const filterBtn = this.el.querySelector(
      '.employee-grid__mobile-filter-btn'
    );
    const drawer = this.el.querySelector('.employee-grid__mobile-drawer');
    const drawerClose = this.el.querySelector(
      '.employee-grid__mobile-drawer-close'
    );
    const overlay = this.el.querySelector('.employee-grid__mobile-overlay');
    const clearBtn = this.el.querySelector(
      '.employee-grid__mobile-drawer-clear'
    );
    const applyBtn = this.el.querySelector(
      '.employee-grid__mobile-drawer-apply'
    );

    const openDrawer = () => {
      drawer?.classList.add('is-open');
      overlay?.classList.add('is-visible');
      document.body.style.overflow = 'hidden';
    };

    const closeDrawer = () => {
      drawer?.classList.remove('is-open');
      overlay?.classList.remove('is-visible');
      document.body.style.overflow = '';
    };

    filterBtn?.addEventListener('click', openDrawer);
    drawerClose?.addEventListener('click', closeDrawer);
    overlay?.addEventListener('click', closeDrawer);
    applyBtn?.addEventListener('click', closeDrawer);
    clearBtn?.addEventListener('click', () => this._clearAllFilters());
  }

  _bindFilters() {
    this.el.addEventListener('change', (e) => {
      if (!e.target.matches('.employee-grid__facet-checkbox')) return;

      const item = e.target.closest('.employee-grid__facet-item');
      item?.classList.toggle(
        'employee-grid__facet-item--selected',
        e.target.checked
      );

      this._fetchResults(1);
    });
  }

  _bindClearAll() {
    const btn = this.el.querySelector('.employee-grid__clear-all-btn');
    btn?.addEventListener('click', () => this._clearAllFilters());
  }

  _bindFilterPanelClear() {
    this.el.addEventListener('click', (e) => {
      const btn = e.target.closest('.employee-grid__filter-clear');
      if (!btn) return;

      const panel = btn.closest('.employee-grid__filter-panel');
      const checkboxes = panel?.querySelectorAll(
        '.employee-grid__facet-checkbox'
      );

      for (let i = 0; i < (checkboxes?.length || 0); i += 1) {
        checkboxes[i].checked = false;
        checkboxes[i]
          .closest('.employee-grid__facet-item')
          ?.classList.remove('employee-grid__facet-item--selected');
      }

      this._fetchResults(1);
    });
  }

  _clearAllFilters() {
    const checkboxes = this.el.querySelectorAll(
      '.employee-grid__facet-checkbox'
    );

    for (let i = 0; i < checkboxes.length; i += 1) {
      checkboxes[i].checked = false;
      checkboxes[i]
        .closest('.employee-grid__facet-item')
        ?.classList.remove('employee-grid__facet-item--selected');
    }

    this._fetchResults(1);
  }

  /**
   * Filter groups sent to the REST route. Must match the keys of
   * EmployeeGrid::FILTER_TAXONOMIES in src/Blocks/EmployeeGrid.php.
   * @returns {string[]} Filter group keys.
   */
  static get FILTER_KEYS() {
    return ['teams', 'divisions', 'locations'];
  }

  _getSelectedValues(name) {
    return Array.from(
      this.el.querySelectorAll(
        `.employee-grid__facet-checkbox[name="${name}[]"]:checked`
      )
    ).map((cb) => cb.value);
  }

  async _fetchResults(page, scroll = false) {
    if (!this.restUrl) return;

    // `this.restUrl` may already contain a query string (e.g. when pretty
    // permalinks are disabled, WP's `rest_url()` returns something like
    // `.../index.php?rest_route=/vital-strategies/v1/employee-grid`). Using
    // `new URL()` + `.searchParams` lets us add params correctly either way
    // instead of blindly appending a second literal `?`.
    const url = new URL(this.restUrl, window.location.origin);

    url.searchParams.set('page', page);
    url.searchParams.set('per_page', this.perPage);

    EmployeeGrid.FILTER_KEYS.forEach((key) => {
      this._getSelectedValues(key).forEach((v) =>
        url.searchParams.append(`${key}[]`, v)
      );
    });

    this.gridEl?.classList.add('is-loading');

    try {
      const res = await fetch(url.toString());
      const data = await res.json();

      this._render(data);

      if (scroll) {
        const offset = 100;
        const top =
          this.el.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    } catch (err) {
      window.console.warn('[EmployeeGrid] Failed to fetch results:', err);
    } finally {
      this.gridEl?.classList.remove('is-loading');
    }
  }

  _render(data) {
    const employees = data?.employees || [];
    const pagination = data?.pagination || { current: 1, total: 1 };

    if (this.gridEl) {
      if (!employees.length) {
        this.gridEl.innerHTML =
          '<p class="employee-grid__empty">No team members found.</p>';
      } else {
        this.gridEl.innerHTML = employees
          .map((employee) =>
            EmployeeGrid._cardTemplate(employee, this.placeholder)
          )
          .join('');
      }
    }

    this._renderPagination(pagination.current, pagination.total);
  }

  static _cardTemplate(employee, placeholder) {
    const thumb = employee.thumbnail
      ? `<img src="${employee.thumbnail}" alt="" class="employee-grid__card-image" loading="lazy">`
      : `<img src="${placeholder}" alt="" class="employee-grid__card-image employee-grid__card-image--placeholder" loading="lazy">`;

    return `
      <article class="employee-grid__card">
        <div class="employee-grid__card-media">${thumb}</div>
        <div class="employee-grid__card-body">
          <h3 class="employee-grid__card-name">${employee.title}</h3>
          ${
            employee.excerpt
              ? `<p class="employee-grid__card-role">${employee.excerpt}</p>`
              : ''
          }
          ${
            employee.employee_url
              ? `<a href="${employee.employee_url.url}" target="${employee.employee_url.target}" class="employee-grid__card-link">View Bio</a>`
              : ''
          }
        </div>
      </article>
    `;
  }

  static _buildPaginationPages(current, total) {
    const pages = [];

    if (total <= 3) {
      for (let i = 1; i <= total; i += 1) {
        pages.push(i);
      }
    } else if (current <= 3) {
      const lastLeadingPage = Math.min(current === 3 ? 4 : 3, total);
      for (let i = 1; i <= lastLeadingPage; i += 1) pages.push(i);
      pages.push(total);
    } else if (current >= total - 2) {
      pages.push(1, total - 2, total - 1, total);
    } else {
      pages.push(1, current - 1, current, current + 1, total);
    }

    return [...new Set(pages)]
      .sort((a, b) => a - b)
      .reduce((items, page, index, visiblePages) => {
        if (index > 0 && page - visiblePages[index - 1] > 1) {
          items.push({ ellipsis: true });
        }
        items.push({ num: page, current: page === current });
        return items;
      }, []);
  }

  _renderPagination(current, total) {
    if (!this.paginationMount) return;

    if (!total || total <= 1) {
      this.paginationMount.innerHTML = '';
      return;
    }

    const pages = EmployeeGrid._buildPaginationPages(current, total);

    const pageItems = pages
      .map((p) => {
        if (p.ellipsis) {
          return '<li class="ellipsis"><span>&hellip;</span></li>';
        }
        if (p.current) {
          return `<li class="current"><span class="current">${p.num}</span></li>`;
        }
        return `<li><a href="#" data-page="${p.num}">${p.num}</a></li>`;
      })
      .join('');

    const arrow = `
      <svg width="20" height="15" viewBox="0 0 22 17" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path class="arrow-head" d="M12.5 0.710938L20 8.21094L12.5 15.7109" stroke="currentColor" stroke-width="2"/>
        <path class="arrow-body" d="M20 8.21094H0" stroke="currentColor" stroke-width="2"/>
      </svg>
    `;

    const prevItem =
      current > 1
        ? `<li class="prev"><a href="#" data-page="${
            current - 1
          }" aria-label="Previous page">${arrow}</a></li>`
        : `<li class="prev disabled"><button disabled aria-label="Previous page">${arrow}</button></li>`;

    const nextItem =
      current < total
        ? `<li class="next"><a href="#" data-page="${
            current + 1
          }" aria-label="Next page">${arrow}</a></li>`
        : `<li class="next disabled"><button disabled aria-label="Next page">${arrow}</button></li>`;

    this.paginationMount.innerHTML = `
      <nav class="pagination-block employee-grid__pagination">
        <ul class="pagination">
          ${prevItem}
          ${pageItems}
          ${nextItem}
        </ul>
      </nav>
    `;

    // Rebind on every render — the markup above is freshly created each time.
    this.paginationMount.querySelectorAll('[data-page]').forEach((link) => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const page = parseInt(link.dataset.page, 10);
        if (Number.isNaN(page)) return;
        this._fetchResults(page, true);
      });
    });
  }
}

export default EmployeeGrid;
